import {
  Button,
  Link,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TextInput,
} from "@carbon/react";
import { Edit } from "@carbon/icons-react";
import i18next from "../../i18n";
import { ServiceLine } from "../../types";
import {
  allLinesPriced,
  CURRENCY_LIST,
  currencyFormatter,
  formatPriceInput,
  isLinePriced,
  lineSubtotal,
  LinePriceInputs,
  linePriceTotals,
  parsePriceInput,
} from "../../utils";
import "./linePriceTable.scss";

type LinePriceTableProps = {
  lines: ServiceLine[];
  prices: LinePriceInputs;
  /** Makes each unit price editable; read-only without it. */
  onPriceChange?: (code: string, unitPrice: string) => void;
  /** Makes each line's currency selectable. */
  onCurrencyChange?: (code: string, currency: string) => void;
  disabled?: boolean;
  /** Marks the lines still without a price. */
  showMissing?: boolean;
  /** Line detail and files under each description; off where the lines are already shown above. */
  showDetail?: boolean;
  /** Offers to re-price the lines, from the table itself, while it is read-only. */
  onStartEdit?: () => void;
};

/** "ARS 380,000.00", or "ARS 150,000.00 + USD 230.00" when the lines use several currencies. */
export const formatTotals = (totals: { currency: string; total: number }[]) =>
  totals.map((t) => currencyFormatter(t.total, t.currency)).join(" + ");

/**
 * SCRUM-116: a service quoted by line, priced line by line — Línea | Cantidad | Moneda | Precio
 * unitario | Subtotal, and the service's total, one per currency. The supplier edits it when
 * quoting; the supplier and the buyer read it afterwards. Column widths are fixed so typing or a
 * missing price never moves the table.
 */
const LinePriceTable = ({
  lines,
  prices,
  onPriceChange,
  onCurrencyChange,
  disabled = false,
  showMissing = false,
  showDetail = true,
  onStartEdit,
}: LinePriceTableProps) => {
  const editable = !!onPriceChange;
  const money = (value: number, currency?: string) =>
    currencyFormatter(value, currency || "USD");
  return (
    <div className="line-prices">
      {onStartEdit && !editable && (
        <div className="line-prices__toolbar">
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Edit}
            onClick={onStartEdit}
          >
            {i18next.t("linePricing.editPrices")}
          </Button>
        </div>
      )}
      <Table
        size="sm"
        className="line-prices__table"
        aria-label={i18next.t("linePricing.title")}
      >
        <colgroup>
          <col />
          <col className="line-prices__col-qty" />
          {editable && <col className="line-prices__col-currency" />}
          <col className="line-prices__col-price" />
          <col className="line-prices__col-subtotal" />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableHeader>{i18next.t("linePricing.line")}</TableHeader>
            <TableHeader className="line-prices__num">
              {i18next.t("linePricing.quantity")}
            </TableHeader>
            {editable && (
              <TableHeader>{i18next.t("linePricing.currency")}</TableHeader>
            )}
            <TableHeader className="line-prices__num">
              {i18next.t("linePricing.unitPrice")}
            </TableHeader>
            <TableHeader className="line-prices__num">
              {i18next.t("linePricing.subtotal")}
            </TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((line) => {
            const code = line.code!;
            const price = prices[code];
            const priced = isLinePriced(prices, code);
            const missing = showMissing && !priced;
            return (
              <TableRow key={code}>
                <TableCell>
                  <span className="line-prices__code">{code}</span>{" "}
                  {line.description}
                  {showDetail && line.note && (
                    <p className="line-prices__note">{line.note}</p>
                  )}
                  {showDetail && (line.files ?? []).length > 0 && (
                    <div className="line-prices__files">
                      {(line.files ?? []).map((file) => (
                        <Link
                          key={file.fileURL}
                          href={file.fileURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="sm"
                        >
                          {file.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell className="line-prices__num">
                  {line.quantity}{" "}
                  {i18next.t(`serviceDetail.unitShort.${line.unit}`)}
                </TableCell>
                {editable && (
                  <TableCell className="line-prices__currency">
                    <Select
                      id={`line-currency-${code}`}
                      size="sm"
                      hideLabel
                      labelText={`${i18next.t("linePricing.currency")} ${code}`}
                      disabled={disabled || !onCurrencyChange}
                      value={price?.currency ?? ""}
                      onChange={(e) => onCurrencyChange?.(code, e.target.value)}
                    >
                      {CURRENCY_LIST.map((c) => (
                        <SelectItem key={c.id} value={c.name} text={c.name} />
                      ))}
                    </Select>
                  </TableCell>
                )}
                <TableCell className="line-prices__num">
                  {editable ? (
                    <TextInput
                      id={`line-price-${code}`}
                      size="sm"
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      hideLabel
                      labelText={`${i18next.t("linePricing.unitPrice")} ${code}`}
                      placeholder="0"
                      disabled={disabled}
                      className={
                        missing ? "line-prices__input--missing" : undefined
                      }
                      aria-invalid={missing || undefined}
                      value={formatPriceInput(price?.unitPrice ?? "")}
                      onChange={(e) =>
                        onPriceChange!(code, parsePriceInput(e.target.value))
                      }
                    />
                  ) : priced ? (
                    money(Number(price.unitPrice), price.currency)
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell className="line-prices__num">
                  {priced
                    ? money(lineSubtotal(line, price.unitPrice), price.currency)
                    : "—"}
                </TableCell>
              </TableRow>
            );
          })}
          <TableRow className="line-prices__total">
            <TableCell colSpan={2}>{i18next.t("linePricing.total")}</TableCell>
            {/* Spans the money columns: a total in several currencies needs the room. */}
            <TableCell
              colSpan={editable ? 3 : 2}
              className="line-prices__num line-prices__grand"
            >
              <strong>
                {formatTotals(linePriceTotals(lines, prices)) || "—"}
              </strong>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {showMissing && !allLinesPriced(lines, prices) && (
        <p className="line-prices__missing" role="alert">
          {i18next.t("linePricing.missingAll")}
        </p>
      )}
    </div>
  );
};

export default LinePriceTable;
